/*
 * label-maker/sheets.js — the sheet-geometry core.
 *
 * WHAT THIS SOLVES, AND WHY IT IS THE WHOLE TOOL
 * ──────────────────────────────────────────────
 * Printing on die-cut label paper fails in exactly three ways, and every one
 * of them is invisible until a sheet is already ruined:
 *
 *   1. THE DRIVER RESCALES. "Fit to page" shrinks the sheet by 2-6 %. At the
 *      top of the page nobody notices; by row 13 the error has accumulated
 *      into whole millimetres and the last row prints across the die cuts.
 *      This is a MULTIPLICATIVE error and it is the most common one.
 *   2. THE PRINTER FEEDS OFF-CENTRE. Every mechanism has its own origin
 *      offset, typically 0.5-2 mm, and it differs per tray and per paper
 *      weight. This is an ADDITIVE error.
 *   3. THE TEMPLATE IS WRONG. Vendors publish the label size and the count,
 *      and almost never the margins and the pitch — which are the numbers
 *      that actually decide where the ink lands.
 *
 * So geometry here is never "a template we hope is right". Every format
 * carries a `src` saying where its numbers come from, the grid is validated
 * against the physical page, and a printer correction (scale + dx/dy) is a
 * first-class part of the model rather than an afterthought. `verifyGrid()`
 * exists so the UI can show the user, before they spend a sheet, that the
 * numbers close: margins that sum to the page, gaps that are not negative,
 * and no label sitting in the printer's unprintable border.
 *
 * `src` values:
 *   'exact'    — a whole-page division: w = 210/cols, h = 297/rows, no
 *                margins, no gaps. Arithmetic, not a measurement. These are
 *                the "N делений" sheets sold across the RU market, and several
 *                are independently confirmed by real vendor templates below.
 *   'spec'     — margins and pitch from a vendor template (glabels product
 *                database, values in PostScript points, converted here).
 *   'derived'  — label size and count are published, the layout is inferred as
 *                a centred symmetric grid. Usable, but the UI must say so and
 *                point at the overlay check.
 *   'roll'     — a thermal-printer roll: the page IS the label, one label per
 *                page (see rolls() below). Kept out of all(): /label-maker is a
 *                sheet tool. A typed roll size carries 'custom'.
 *
 * Pure module: no DOM, no browser API. `node --test` drives it directly, which
 * is how the arithmetic is checked without a printer.
 */
(function (root, factory) {
    'use strict';
    var api = factory();
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
    if (root) root.LabelSheets = api;
})(typeof self !== 'undefined' ? self : (typeof window !== 'undefined' ? window : null), function () {
    'use strict';

    /* f4 — the Indonesian office sheet (kertas F4 / Folio, 215 × 330 mm): the
     * paper an UMKM prints its sticker sheet on (/halal-label, 23.10.2026).
     * Printer drivers call it "F4" or "Folio" (8.5 × 13 in = 215.9 × 330.2 mm);
     * the 0.9 mm difference is far inside any centred grid's margin. A named
     * page only: no registry format uses it, so all() is unchanged. */
    var PAGES = {
        a4:     { w: 210,   h: 297 },
        a5:     { w: 148,   h: 210 },
        letter: { w: 215.9, h: 279.4 },
        f4:     { w: 215,   h: 330 },
    };

    /* The border no consumer printer can mark. Inkjets manage ~3 mm on three
     * sides and need ~9-14 mm at the trailing edge for the feed rollers;
     * lasers are ~4-5 mm all round. 5 mm is the honest common floor, and it is
     * a WARNING threshold, not a hard block: borderless and A3-carrier setups
     * do exist, and a full-bleed 1-up sheet is a legitimate thing to attempt.
     */
    var DEAD_ZONE_MM = 5;

    /* Smallest side a label may have. Below this it is a typo, not a format. */
    var MIN_SIDE_MM = 1;

    /* Convert a glabels-style value in PostScript points to millimetres. The
     * vendor database is authored in points; keeping the conversion here means
     * the table below reads in the unit the rest of the tool speaks. */
    function pt(v) { return v * 25.4 / 72; }

    /* ── whole-page division sheets ──────────────────────────────────────────
     * The dominant RU product line ("этикетки А4, N делений"): the sheet is cut
     * into an exact grid with no waste, so the geometry is arithmetic. Where a
     * real vendor template exists for the same count it is noted — every one of
     * those confirms x0 = y0 = 0 and pitch = label size.
     */
    var DIVISIONS = [
        { n: 1,  cols: 1, rows: 1  },
        { n: 2,  cols: 1, rows: 2  },
        { n: 4,  cols: 2, rows: 2,  note: 'Herma 10809' },
        { n: 6,  cols: 2, rows: 3  },
        { n: 8,  cols: 2, rows: 4  },
        { n: 10, cols: 2, rows: 5  },
        { n: 12, cols: 2, rows: 6  },
        { n: 14, cols: 2, rows: 7,  note: 'Herma 4625' },
        { n: 16, cols: 2, rows: 8,  note: 'Herma 4620' },
        { n: 18, cols: 2, rows: 9  },
        { n: 20, cols: 2, rows: 10 },
        { n: 21, cols: 3, rows: 7,  note: 'Viking 02204' },
        { n: 24, cols: 3, rows: 8,  note: 'Bruna 1166000' },
        { n: 27, cols: 3, rows: 9  },
        { n: 30, cols: 3, rows: 10 },
        { n: 33, cols: 3, rows: 11 },
        { n: 40, cols: 4, rows: 10 },
        { n: 48, cols: 4, rows: 12 },
        { n: 70, cols: 5, rows: 14 },
    ];

    /* ── vendor templates with published margins and pitch ───────────────────
     * Values are the ones the vendor database carries, converted from points.
     * These sheets have real gaps and rounded corners, so they cannot be
     * derived: 21-up Avery is 63.99 x 38.1 on a 66.04 pitch, which is NOT the
     * 70 mm of the same-count division sheet. Picking the wrong one of those
     * two puts every label 2 mm out by the third column.
     */
    var VENDOR = [
        // part,        cols,rows, w,        h,        x0,       y0,       dx,        dy,       radius
        ['L7651',  5, 13, pt(108),    pt(60.09),  pt(13.32),   pt(30.90),  pt(115.09), pt(60.09),  pt(5)],
        ['L7160',  3,  7, pt(181.4),  pt(108),    pt(21.2),    pt(43.9),   pt(187.2),  pt(108),    pt(5)],
        ['L7161',  3,  6, pt(180.2),  pt(132.6),  pt(21),      pt(23),     pt(186.9),  pt(132.5),  pt(7)],
        ['L7162',  2,  8, pt(280.9),  pt(96.1),   pt(11.3),    pt(36.8),   pt(290.5),  pt(96.1),   pt(5)],
        ['L7163',  2,  7, pt(280.9),  pt(108),    pt(9.5),     pt(43),     pt(292),    pt(108),    pt(5)],
        ['L7164',  3,  4, pt(180),    pt(204),    pt(20.84),   pt(10),     pt(187.09), pt(204),    pt(8.5)],
        ['L7165',  2,  4, pt(280.8),  pt(191.99), pt(13.2378), pt(36.96),  pt(288.11), pt(191.99), 0],
        ['L7169',  2,  2, pt(280.9),  pt(394),    pt(14.2),    pt(20),     pt(287.7),  pt(394),    pt(6)],
    ];

    /* ── US Letter ───────────────────────────────────────────────────────────
     * The North American market has no "N divisions" line at all: it is Avery
     * part numbers on 8.5 × 11". Their geometry is authored in whole inches,
     * which is why it is written that way here — 5160 is a 2.625 × 1" label on
     * a 2.78125" pitch, and rounding that through millimetres first would throw
     * away the exactness for no reason.
     */
    function inch(v) { return v * 25.4; }

    var VENDOR_US = [
        // part,   cols,rows,  w,             h,                x0,           y0,          dx,             dy,               radius
        ['5160',  3, 10, inch(2.625), inch(1),          inch(0.15625), inch(0.5),   inch(2.78125), inch(1),          inch(0.0625)],
        ['5161',  2, 10, inch(4),     inch(1),          inch(0.15625), inch(0.5),   inch(4.1875),  inch(1),          inch(0.0625)],
        ['5162',  2,  7, inch(4),     inch(4 / 3),      inch(0.15625), inch(5 / 6), inch(4.1875),  inch(4 / 3),      inch(0.0625)],
        ['5163',  2,  5, inch(4),     inch(2),          inch(0.1625),  inch(0.5),   inch(4.1875),  inch(2),          inch(0.125)],
        ['5164',  2,  3, inch(4),     inch(10 / 3),     inch(0.15625), inch(0.5),   inch(4.1875),  inch(10 / 3),     inch(0.125)],
        ['5167',  4, 20, inch(1.75),  inch(0.5),        inch(0.28125), inch(0.5),   inch(2.0625),  inch(0.5),        inch(0.0625)],
        ['5395',  2,  4, inch(3.375), inch(7 / 3),      inch(0.6875),  inch(7 / 12), inch(3.75),   inch(2.5),        inch(0.1875)],
    ];

    /* ── round-corner / small-format sheets that are neither ────────────────
     * Kept separate so the `derived` badge is impossible to lose: the count and
     * label size come from the product, the placement is a centred grid.
     */
    var DERIVED = [
        // id,            cols, rows, w,    h,    shape,    radius
        ['round-40',        4,  6,  40,   40,   'circle', 0],
        ['round-30',        6,  9,  30,   30,   'circle', 0],
        ['round-60',        3,  4,  60,   60,   'circle', 0],
        ['folder-190x38',   1,  7,  190,  38,   'rect',   0],
        ['folder-190x25',   1,  10, 190,  25,   'rect',   0],
        /* Две работы, которые люди ищут чаще, чем «наклейки на банки», а
           геометрии под них у нас не было (замер Wordstat 2026-08-20):
             «визитки распечатать»    2 959/мес — 90×50 мм, российский стандарт;
             «этикетка для озон»      3 309  +  «для вайлдберриз» 1 490 —
                                      маркетплейсы просят 58×40 мм.
           На A4 обе раскладываются целыми числами: 2×5 визиток (180×250 мм) и
           3×6 маркетплейсных (174×240 мм) — обе с запасом на поля принтера.
           ⚠️ У маркетплейсных именно 6 рядов, а не 7: при семи поля сверху и
           снизу выходят 2,1 мм, и verifyGrid честно ругается dead_zone —
           принтер такую кромку обрежет. Шесть дают 28,5 мм и 18 штук на
           лист, то есть ровно столько, сколько у МойСклада стоит денег. */
        ['card-90x50',      2,  5,  90,   50,   'rect',   0, 'job'],
        ['mk-58x40',        3,  6,  58,   40,   'rect',   0, 'job'],
    ];

    /* Build the immutable format list once. */
    var FORMATS = (function () {
        var out = [];

        /* NOTE ON PRECISION. Nothing here is rounded. A division sheet is
         * exact by construction — 297/7 is 42.428571…, and storing 42.429
         * makes seven rows add up to 297.003, i.e. the last row hangs off the
         * paper and `verifyGrid` reports a negative bottom margin on two of
         * the commonest RU formats. Rounding belongs to the UI (`fmt`), never
         * to the model. */
        DIVISIONS.forEach(function (d) {
            var page = PAGES.a4;
            var w = page.w / d.cols, h = page.h / d.rows;
            out.push({
                id: 'div-' + d.n,
                family: 'division',
                page: 'a4',
                cols: d.cols, rows: d.rows,
                w: w, h: h,
                x0: 0, y0: 0,
                dx: w, dy: h,
                shape: 'rect', radius: 0,
                src: 'exact',
                confirmedBy: d.note || null,
            });
        });

        VENDOR.forEach(function (v) {
            out.push({
                id: 'avery-' + v[0].toLowerCase(),
                family: 'vendor',
                vendor: 'Avery',
                part: v[0],
                page: 'a4',
                cols: v[1], rows: v[2],
                w: v[3], h: v[4],
                x0: v[5], y0: v[6],
                dx: v[7], dy: v[8],
                shape: v[9] > 0.6 ? 'round' : 'rect',
                radius: v[9],
                src: 'spec',
                confirmedBy: null,
            });
        });

        VENDOR_US.forEach(function (v) {
            out.push({
                id: 'avery-' + v[0],
                family: 'vendor-us',
                vendor: 'Avery',
                part: v[0],
                page: 'letter',
                cols: v[1], rows: v[2],
                w: v[3], h: v[4],
                x0: v[5], y0: v[6],
                dx: v[7], dy: v[8],
                shape: v[9] > 0.6 ? 'round' : 'rect',
                radius: v[9],
                src: 'spec',
                confirmedBy: null,
            });
        });

        DERIVED.forEach(function (d) {
            var g = centredGrid('a4', d[1], d[2], d[3], d[4]);
            out.push({
                id: d[0],
                /* Восьмой столбец — семейство для группировки в списке. Круглые
                   и корешки остаются 'derived', а работы с собственным спросом
                   («визитки», «маркетплейс») выделены в 'job': в выпадающем
                   списке человек ищет свою ЗАДАЧУ, а не способ, которым мы
                   вывели геометрию. */
                family: d[7] || 'derived',
                page: 'a4',
                cols: d[1], rows: d[2],
                w: d[3], h: d[4],
                x0: g.x0, y0: g.y0, dx: g.dx, dy: g.dy,
                shape: d[5], radius: d[6],
                src: 'derived',
                confirmedBy: null,
            });
        });

        return out;
    })();

    function round3(v) { return Math.round(v * 1000) / 1000; }

    /* Display rounding. The model keeps full precision (see the note in the
     * registry builder); anything the user reads goes through here. */
    function fmt(v, dp) {
        var d = dp == null ? 1 : dp;
        var n = Math.round(v * Math.pow(10, d)) / Math.pow(10, d);
        return (Object.is(n, -0) ? 0 : n).toFixed(d).replace(/\.0+$/, '').replace(/(\.\d*?)0+$/, '$1');
    }

    /* A page is named ('a4', 'letter') for sheets and GIVEN for rolls, where the
     * page is the label itself ({w, h} in mm). Everything downstream — preview,
     * PDF, overlay, correction — asks this one function, so a roll needs no
     * second code path anywhere. */
    function page(id) {
        if (id && typeof id === 'object' && id.w > 0 && id.h > 0) return { w: id.w, h: id.h };
        return PAGES[id] || PAGES.a4;
    }

    /* ── rolls: ONE LABEL PER PAGE ───────────────────────────────────────────
     * A thermal printer (Xprinter, TSC, Godex, Zebra, the marketplace sellers'
     * standard) does not print sheets. It prints a strip of labels separated by
     * a gap, and its driver treats every label as a page: the gap sensor finds
     * the next label, the page is printed onto it. So the PDF for a roll has a
     * page box EQUAL to the label — 58 × 40 mm, one label per page — and any
     * other page size is the classic failure: a 60 × 35 template "fit to page"
     * onto 58 × 40 stock, or an A4 page of which the printer prints the top-left
     * corner.
     *
     * Kept out of `all()` on purpose. /label-maker is a SHEET tool — on a roll
     * there is no die cut to hit, and hitting the die cut is the whole of its
     * point — so its selector must not grow roll entries by accident. Roll
     * consumers ask `rolls()` / `roll()`; `byId('roll-58x40')` resolves both.
     *
     * The list is the set the marketplaces and the ЧЗ templates actually ask
     * for: 58 × 40 (WB, Ozon FBS, the common ЧЗ label), 58 × 30 and 58 × 60
     * (WB), 43 × 25 and 40 × 30 (small goods, cosmetics), 75 × 120 and
     * 100 × 150 (shipping / box labels). Width is the dimension ACROSS the
     * print head — how roll stock is sold and how drivers name it. */
    var ROLLS = [
        [58, 40], [58, 30], [58, 60], [43, 25], [40, 30], [75, 120], [100, 150],
    ];

    /* Width a desktop thermal head can print (4-inch heads: 104-108 mm) and
     * the height range the ЧЗ template designer itself allows (10-300 mm).
     * Outside these a roll size is a typo. */
    var ROLL_MIN_MM = 10, ROLL_MAX_W_MM = 120, ROLL_MAX_H_MM = 300;

    function rollFormat(w, h, id, src) {
        return {
            id: id || ('roll-' + fmt(w, 2) + 'x' + fmt(h, 2)),
            family: 'roll',
            page: { w: w, h: h },
            cols: 1, rows: 1,
            w: w, h: h,
            x0: 0, y0: 0, dx: w, dy: h,
            shape: 'rect', radius: 0,
            src: src || 'roll',
            confirmedBy: null,
        };
    }

    var ROLL_FORMATS = ROLLS.map(function (r) { return rollFormat(r[0], r[1], 'roll-' + r[0] + 'x' + r[1]); });

    function rolls() { return ROLL_FORMATS.map(copy); }

    /* A roll size the registry does not carry — typed by the person. Same
     * clamping rule as `custom`: a value that cannot be a label never reaches
     * a renderer. */
    function roll(spec) {
        spec = spec || {};
        var w = Math.min(ROLL_MAX_W_MM, Math.max(ROLL_MIN_MM, num(spec.w, 58)));
        var h = Math.min(ROLL_MAX_H_MM, Math.max(ROLL_MIN_MM, num(spec.h, 40)));
        for (var i = 0; i < ROLL_FORMATS.length; i++) {
            if (Math.abs(ROLL_FORMATS[i].w - w) < 1e-9 && Math.abs(ROLL_FORMATS[i].h - h) < 1e-9) return copy(ROLL_FORMATS[i]);
        }
        return rollFormat(w, h, null, 'custom');
    }

    function isRoll(f) { return !!(f && f.family === 'roll'); }

    /* ── the roll test page ──────────────────────────────────────────────────
     * The sheet tool closes its loop with an overlay sheet held against the die
     * cuts. A roll has no die cut to hold anything against; what goes wrong on a
     * thermal printer is SCALE and RESAMPLING — "fit to page" in the dialog, a
     * driver set to the wrong paper, a 300-dpi page sent to a 203-dpi head. The
     * last one is invisible on a ruler (a few per cent) and fatal for a
     * DataMatrix, whose module has to be a whole number of dots.
     *
     * So the test label carries, in label millimetres:
     *   - a horizontal ruler (50 mm where the label is wide enough, otherwise
     *     the longest whole 10 mm that fits) and a vertical one — measure them;
     *   - a dot patch: four quadrants of one-dot patterns drawn ON the printer's
     *     dot grid (stripes both ways, a 1-dot and a 2-dot checkerboard). At
     *     100 % on the right dpi all four print as the same even grey; any
     *     resampling turns them into visibly different shades or moiré — the
     *     one test for "my code will print sharp" that needs no scanner;
     *   - a slot for a sample DataMatrix at the real module size;
     *   - a caption line.
     * Pure geometry; pdf.js draws it. `dpi` is the printer's, `dotMm` derives. */
    function rollTestPlan(f, opts) {
        opts = opts || {};
        var dpi = num(opts.dpi, 203);
        if (!(dpi >= 100 && dpi <= 1200)) dpi = 203;
        var dotMm = 25.4 / dpi;
        var p = page(f.page);
        var m = 2;                                   // edge clearance, mm

        // Rulers: whole 10 mm only — a 47 mm "ruler" is a number nobody can check.
        var hLen = p.w - 2 * m >= 50 ? 50 : Math.max(10, Math.floor((p.w - 2 * m - 2) / 10) * 10);
        var ruler = { x: (p.w - hLen) / 2, y: m + 3.2, len: hLen, tick: 2.6 };
        var top = ruler.y + 3.4;                     // below ruler + its caption
        var vSpace = p.h - top - m;
        var vLen = vSpace >= 12 ? Math.min(50, Math.floor((vSpace - 2) / 10) * 10) : 0;
        var vruler = vLen >= 10 ? { x: m + 0.2, y: top + 1, len: vLen, tick: 2.2 } : null;

        // Dot patch: a square of WHOLE dots, its origin snapped to the dot grid
        // of the page — otherwise a "one dot" stripe straddles two dots by
        // construction and the patch tests our arithmetic, not the printer.
        var free = p.h - top - m;
        var patchMm = Math.max(4, Math.min(12, free - 1, (p.w - 2 * m) * 0.3));
        var patchDots = Math.max(8, Math.floor(patchMm / dotMm / 4) * 4);   // divisible by 4: two 2-dot cells per quadrant
        patchMm = patchDots * dotMm;
        var px = snap(p.w - m - patchMm, dotMm), py = snap(top + (free - patchMm) / 2, dotMm);
        var patch = { x: px, y: py, dots: patchDots, dotMm: dotMm, mm: patchMm };

        // Sample code slot: between the vertical ruler and the patch.
        var left = vruler ? vruler.x + vruler.tick + 1.6 : m;
        var slot = { x: left, y: top + 0.6, w: Math.max(0, px - 1.5 - left), h: Math.max(0, p.h - top - 0.6 - m) };

        return { dpi: dpi, dotMm: dotMm, page: p, ruler: ruler, vruler: vruler, patch: patch, sample: slot };
    }

    function snap(v, step) { return Math.round(v / step) * step; }

    /* Lay `cols x rows` labels of w x h on a page, centred, with the leftover
     * space split evenly into the gaps AND the margins. This is the layout a
     * sane manufacturer picks, which is why it is the right guess when only the
     * label size and the count are published — but it is still a guess, hence
     * `src:'derived'` on everything that uses it. */
    function centredGrid(pageId, cols, rows, w, h) {
        var p = page(pageId);
        var freeX = p.w - cols * w, freeY = p.h - rows * h;
        var gapX = cols > 1 ? freeX / (cols + 1) : 0;
        var gapY = rows > 1 ? freeY / (rows + 1) : 0;
        return {
            x0: cols > 1 ? gapX : freeX / 2,
            y0: rows > 1 ? gapY : freeY / 2,
            dx: w + gapX,
            dy: h + gapY,
        };
    }

    function all() { return FORMATS.slice(); }

    function byId(id) {
        for (var i = 0; i < FORMATS.length; i++) if (FORMATS[i].id === id) return copy(FORMATS[i]);
        for (var j = 0; j < ROLL_FORMATS.length; j++) if (ROLL_FORMATS[j].id === id) return copy(ROLL_FORMATS[j]);
        return null;
    }

    function copy(f) {
        var o = {};
        for (var k in f) if (Object.prototype.hasOwnProperty.call(f, k)) o[k] = f[k];
        // A roll's page is an object: hand out a copy of it too, or a caller
        // editing its format would resize the registry's label.
        if (o.page && typeof o.page === 'object') o.page = { w: o.page.w, h: o.page.h };
        return o;
    }

    /* A user-authored grid. Same shape as a registry entry so everything
     * downstream — preview, PDF, overlay — stays unaware of the difference. */
    function custom(spec) {
        spec = spec || {};
        var pageId = PAGES[spec.page] ? spec.page : 'a4';
        var cols = clampInt(spec.cols, 1, 40, 1);
        var rows = clampInt(spec.rows, 1, 60, 1);
        var p = page(pageId);
        /* A NEGATIVE OR ZERO SIDE IS NOT A GRID, AND IT MUST NOT REACH A
         * RENDERER. Typing "-20" into the height field produced <rect
         * height="-20"> once per cell per redraw — 584 console errors in one
         * adversarial run — while `verifyGrid` correctly reported the grid as
         * broken. Reporting a value AND passing it on are different things:
         * validation tells the user, clamping protects everything downstream.
         * MIN_SIDE is 1 mm because a die cut smaller than that does not exist. */
        var w = Math.max(MIN_SIDE_MM, num(spec.w, p.w / cols));
        var h = Math.max(MIN_SIDE_MM, num(spec.h, p.h / rows));
        var g = centredGrid(pageId, cols, rows, w, h);
        var shape = spec.shape === 'circle' || spec.shape === 'round' || spec.shape === 'ellipse'
            ? spec.shape : 'rect';
        if (shape === 'circle') h = w;
        return {
            id: 'custom',
            family: 'custom',
            page: pageId,
            cols: cols, rows: rows,
            w: w, h: h,
            x0: num(spec.x0, g.x0),
            y0: num(spec.y0, g.y0),
            dx: num(spec.dx, g.dx),
            dy: num(spec.dy, g.dy),
            shape: shape,
            radius: num(spec.radius, 0),
            src: spec.measured ? 'measured' : 'custom',
            confirmedBy: null,
        };
    }

    function num(v, dflt) {
        v = typeof v === 'string' ? parseFloat(v.replace(',', '.')) : v;
        return (typeof v === 'number' && isFinite(v)) ? v : dflt;
    }
    function clampInt(v, lo, hi, dflt) {
        v = parseInt(v, 10);
        if (!isFinite(v)) return dflt;
        return Math.max(lo, Math.min(hi, v));
    }

    /* ── printer correction ──────────────────────────────────────────────────
     * `scale` is multiplicative and applied ABOUT THE PAGE CENTRE, because that
     * is what "fit to page" actually does: it shrinks the imaged area toward
     * the middle of the sheet, so the top rows drift down and the bottom rows
     * drift up. Correcting about the origin instead would fix the first row and
     * leave the last one exactly as wrong as before.
     *
     * `dx`/`dy` are additive and applied after, in millimetres, positive =
     * right / down, matching what the user reads off the overlay sheet.
     */
    function correction(c) {
        c = c || {};
        var s = num(c.scale, 1);
        if (!(s > 0.8 && s < 1.25)) s = 1;   // outside this a template is wrong, not a printer
        return { scale: s, dx: num(c.dx, 0) || 0, dy: num(c.dy, 0) || 0 };
    }

    function applyCorrection(pageId, x, y, corr) {
        var p = page(pageId), c = correction(corr);
        return {
            x: (x - p.w / 2) * c.scale + p.w / 2 + c.dx,
            y: (y - p.h / 2) * c.scale + p.h / 2 + c.dy,
        };
    }

    /* Top-left corner of label `index` (0-based), in page millimetres, after
     * printer correction. `order` decides whether the sheet fills across rows
     * or down columns — Avery calls this fill direction and it matters the
     * moment a user prints a merged list onto a partly-used sheet. */
    function cellAt(fmt, index, opts) {
        opts = opts || {};
        var perSheet = fmt.cols * fmt.rows;
        var i = ((index % perSheet) + perSheet) % perSheet;
        var col, row;
        if (opts.order === 'col') { col = Math.floor(i / fmt.rows); row = i % fmt.rows; }
        else                      { row = Math.floor(i / fmt.cols); col = i % fmt.cols; }
        var x = fmt.x0 + col * fmt.dx;
        var y = fmt.y0 + row * fmt.dy;
        var p = applyCorrection(fmt.page, x, y, opts.correction);
        return {
            x: p.x, y: p.y, col: col, row: row,
            w: fmt.w * correction(opts.correction).scale,
            h: fmt.h * correction(opts.correction).scale,
        };
    }

    function perSheet(fmt) { return fmt.cols * fmt.rows; }

    /* ── validation ──────────────────────────────────────────────────────────
     * Everything the user needs to know BEFORE spending a sheet. Each finding
     * carries a machine-readable `code` so the UI can phrase it in 9 locales
     * without parsing prose.
     */
    function verifyGrid(fmt) {
        var p = page(fmt.page);
        var findings = [];

        var gapX = fmt.dx - fmt.w;
        var gapY = fmt.dy - fmt.h;
        var rightM = p.w - (fmt.x0 + (fmt.cols - 1) * fmt.dx + fmt.w);
        var bottomM = p.h - (fmt.y0 + (fmt.rows - 1) * fmt.dy + fmt.h);

        if (fmt.w <= 0 || fmt.h <= 0) findings.push({ code: 'size_nonpositive', level: 'error' });
        if (gapX < -0.05) findings.push({ code: 'overlap_x', level: 'error', mm: round3(-gapX) });
        if (gapY < -0.05) findings.push({ code: 'overlap_y', level: 'error', mm: round3(-gapY) });
        if (fmt.x0 < -0.05) findings.push({ code: 'off_left', level: 'error', mm: round3(-fmt.x0) });
        if (fmt.y0 < -0.05) findings.push({ code: 'off_top', level: 'error', mm: round3(-fmt.y0) });
        if (rightM < -0.05) findings.push({ code: 'off_right', level: 'error', mm: round3(-rightM) });
        if (bottomM < -0.05) findings.push({ code: 'off_bottom', level: 'error', mm: round3(-bottomM) });

        // Unprintable border. Warn once per side, with the shortfall, so the
        // user can decide (borderless printers exist) instead of being blocked.
        var edges = [];
        /* A roll has no unprintable border to warn about: the page IS the
         * label, and a thermal head prints it edge to edge (the content inset,
         * layout.SAFE_MM, is what keeps ink off a label that wanders sideways).
         * The office-printer dead zone is the wrong model here — it would greet
         * every roll with an orange warning about a fact that is not true. */
        if (!isRoll(fmt)) {
            if (fmt.x0 < DEAD_ZONE_MM) edges.push('left');
            if (fmt.y0 < DEAD_ZONE_MM) edges.push('top');
            if (rightM < DEAD_ZONE_MM) edges.push('right');
            if (bottomM < DEAD_ZONE_MM) edges.push('bottom');
        }
        if (edges.length) {
            // Severity depends on whether the user can DO anything about it.
            // On a whole-page division sheet the labels reach the paper edge by
            // construction — that is what the product is, and an orange warning
            // on the commonest RU format greets every visitor with an alarm
            // about a fact they cannot change. On a grid the user authored, the
            // same finding is actionable, so it stays a warning.
            findings.push({
                code: 'dead_zone',
                level: fmt.src === 'exact' ? 'info' : 'warn',
                edges: edges, mm: DEAD_ZONE_MM,
            });
        }

        return {
            ok: !findings.some(function (f) { return f.level === 'error'; }),
            findings: findings,
            margins: { left: round3(fmt.x0), top: round3(fmt.y0), right: round3(rightM), bottom: round3(bottomM) },
            gaps: { x: round3(gapX), y: round3(gapY) },
            perSheet: perSheet(fmt),
            coverage: round3((fmt.cols * fmt.rows * fmt.w * fmt.h) / (p.w * p.h) * 100),
        };
    }

    /* Where a free 50 mm scale bar can live on this sheet without touching a
     * label. Returns null when the sheet is full-bleed and there is genuinely
     * no free paper — in that case the tool must not pretend otherwise. */
    function rulerSlot(fmt) {
        var p = page(fmt.page);
        var v = verifyGrid(fmt);
        var BAR = 50, PAD = 2.5, NEED = 6;   // bar length, clearance, band height

        if (v.margins.top >= NEED + PAD && p.w >= BAR + 20) {
            return { x: (p.w - BAR) / 2, y: Math.max(PAD, v.margins.top / 2 - NEED / 2), len: BAR, side: 'top' };
        }
        if (v.margins.bottom >= NEED + PAD && p.w >= BAR + 20) {
            return { x: (p.w - BAR) / 2, y: p.h - v.margins.bottom / 2 - NEED / 2, len: BAR, side: 'bottom' };
        }
        if (v.gaps.y >= NEED + 1) {
            return { x: (p.w - BAR) / 2, y: fmt.y0 + fmt.h + (v.gaps.y - NEED) / 2, len: BAR, side: 'gap' };
        }
        return null;
    }

    /* Solve a printer's scale from a printed reference line: the user prints a
     * line we asked to be `nominal` mm and measures `measured` mm with a ruler.
     * The factor that cancels the error is nominal/measured.
     *
     * Guarded, because a mistyped number here silently poisons every later
     * sheet: beyond ±20 % the sheet was printed at the wrong paper size or the
     * user measured the wrong line, and the honest answer is to say so. */
    function solveScale(nominal, measured) {
        nominal = num(nominal, 0); measured = num(measured, 0);
        if (!(nominal > 0) || !(measured > 0)) return { ok: false, code: 'need_two_numbers' };
        var f = nominal / measured;
        if (f < 0.8 || f > 1.25) return { ok: false, code: 'out_of_range', scale: f };
        return { ok: true, scale: f, offBy: (f - 1) * 100 };
    }

    return {
        PAGES: PAGES,
        DEAD_ZONE_MM: DEAD_ZONE_MM,
        MIN_SIDE_MM: MIN_SIDE_MM,
        page: page,
        all: all,
        byId: byId,
        custom: custom,
        centredGrid: centredGrid,
        perSheet: perSheet,
        cellAt: cellAt,
        correction: correction,
        applyCorrection: applyCorrection,
        verifyGrid: verifyGrid,
        fmt: fmt,
        rulerSlot: rulerSlot,
        solveScale: solveScale,
        rolls: rolls,
        roll: roll,
        isRoll: isRoll,
        rollTestPlan: rollTestPlan,
        ROLL_MIN_MM: ROLL_MIN_MM,
        ROLL_MAX_W_MM: ROLL_MAX_W_MM,
        ROLL_MAX_H_MM: ROLL_MAX_H_MM,
    };
});
